import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { HAS_ACCOUNT_KEY } from '../utils/constants'
import { Button } from '../designsystem/Button'
import { MaxSignature } from './MaxSignature'
import TimSignature from './TimSignature'
import { Map, networkLocations } from './Map'
import { BackgroundGradient } from './BackgroundGradient'
import { Intercom } from 'react-live-chat-loader'
import { DemoVideo } from './DemoVideo'
import { Header } from './marketing/Header'
import { MarketingButton } from '../designsystem/MarketingButton'
import { GitHub } from '../icons/GitHub'
import { ExplainerGraphic } from './marketing/ExplainerGraphic'
import { Marketing, SubHeadline } from '../designsystem/Text'
import { CTASection } from './marketing/CTASection'

export function LandingPage() {
  const [hasAccount, setHasAccount] = useState(false)
  useEffect(() => {
    const account = localStorage.getItem(HAS_ACCOUNT_KEY)
    if (account) {
      setHasAccount(true)
    }
  }, [])

  return (
    <>
      <div className='relative bg-gray-50'>
        <Intercom color='#608BD8' />
        <div className='relative'>
          <main>
            <BackgroundGradient />
            <Header dark />
            <div className='relative max-w-6xl pt-16 mx-auto lg:px-8 sm:pt-6'>
              <div className='lg:flex lg:space-x-4 lg:justify-between lg:items-center'>
                <div className='w-full max-w-xl px-6 mx-auto mb-6 sm:px-6 sm:text-center sm:max-w-2xl lg:px-0 lg:text-left lg:flex-1 lg:flex lg:items-center md:mb-8 lg:my-16'>
                  <div className='w-full space-y-16'>
                    <div className='space-y-8'>
                      <h1 className='text-4xl font-medium leading-tight text-white sm:text-5xl sm:text-center gotham sm:mt-5 lg:mt-6 md:text-7xl xl:leading-tight'>
                        Peace&nbsp;of&nbsp;mind for your GraphQL&nbsp;API
                      </h1>
                      <p className='text-xl font-medium text-white sm:text-center gotham opacity-90 sm:mt-10 md:text-2xl sm:mx-auto lg:-mx-10'>
                        Reduce your origin traffic by up to 95% with our GraphQL
                        edge cache and never worry about scaling again.
                      </p>
                    </div>
                    <div className='flex sm:justify-center'>
                      {hasAccount && false ? (
                        <Link href='/dashboard'>
                          <Button>Go to dashboard</Button>
                        </Link>
                      ) : (
                        <div className='flex flex-col items-center'>
                          <Link href='/api/auth/github'>
                            <a>
                              <MarketingButton>
                                <GitHub className='w-7 h-7' />
                                <div className='ml-3'>Sign up with GitHub</div>
                              </MarketingButton>
                            </a>
                          </Link>
                          <span className='mt-2 text-sm font-medium text-white opacity-90 gotham'>
                            Free up to 5M req/mo · No CC required
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='px-6 my-24 sm:px-10'>
              <div className='mx-auto max-w-7xl'>
                <DemoVideo />
              </div>
            </div>

            <div className='px-6 mt-24 sm:px-6 sm:mt-32 lg:mt-40'>
              <div className='mx-auto max-w-7xl'>
                <div className='space-y-2 text-center'>
                  <SubHeadline>How it works</SubHeadline>
                  <Marketing.H2>The GraphQL CDN</Marketing.H2>
                </div>
              </div>
            </div>
            <div className='relative mt-4 lg:mt-10'>
              <div className='max-w-6xl mx-auto overflow-x-scroll sm:overflow-x-auto'>
                <ExplainerGraphic className='h-48 px-6 mt-8 sm:h-auto sm:w-full' />
              </div>
              {/* This hints at the graphic being scrollable on mobile */}
              <div
                className='absolute top-0 bottom-0 right-0 block w-8 sm:hidden'
                style={{
                  background:
                    'linear-gradient(90deg, rgba(249,250,252,0) 0%, rgba(249,250,252,1) 100%, rgba(249,250,252,1) 100%);',
                }}
              />
            </div>
            <div className='px-6 mb-24 sm:px-6 sm:mb-32 lg:mb-40'>
              <p className='max-w-3xl mx-auto text-base font-medium text-left mt-14 sm:text-center sm:text-lg gotham text-gray opacity-60'>
                Our GraphQL edge cache serves cached query results to your users
                all around the world in milliseconds. If a mutation changes some
                data, the smart purging automatically invalidates any cached
                query that contains that data.
              </p>
            </div>

            <div className='px-6 my-24 sm:px-6 sm:my-32 lg:my-40'>
              <div className='mx-auto space-y-16 max-w-7xl'>
                <div className='space-y-2 text-center'>
                  <SubHeadline>All-in-one</SubHeadline>
                  <Marketing.H2 className='max-w-2xl mx-auto'>
                    Scale, inspect &amp; protect your GraphQL API
                  </Marketing.H2>
                </div>
                <div className='flex flex-col space-y-8 lg:space-y-0 lg:space-x-8 lg:flex-row gotham'>
                  <div className='flex flex-col space-y-8' style={{ flex: 3 }}>
                    <div className='flex flex-col flex-1 space-y-4 bg-white rounded-lg shadow-lg md:space-y-0 md:space-x-4 md:flex-row'>
                      <div className='p-8 space-y-4' style={{ flex: 2 }}>
                        <Marketing.H3>Caching</Marketing.H3>
                        <p className='text-base sm:text-lg text-gray opacity-80'>
                          Reduce your origin traffic by up to 95%. Achieve
                          super-fast performance by caching GraphQL query
                          results at the edge.
                        </p>
                      </div>
                      <div
                        className='flex items-center justify-start flex-1 p-8 rounded-b-lg md:rounded-l-none md:rounded-r-lg md:justify-center md:items-center'
                        style={{
                          background: `linear-gradient(180deg, #293B5B 0%, #0C1F40 100%), #293B5B`,
                        }}
                      >
                        <RuleCode />
                      </div>
                    </div>
                    <div className='flex flex-col-reverse items-start flex-1 overflow-hidden bg-white rounded-lg shadow-lg md:flex-row'>
                      <div className='w-full h-64 md:h-full bg-gray-50'>
                        <img
                          src='/malicious-query.svg'
                          className='w-auto h-full'
                        />
                      </div>
                      <div className='p-8 space-y-4'>
                        <Marketing.H3>Security</Marketing.H3>
                        <p className='text-base sm:text-lg text-gray opacity-80'>
                          Protect your GraphQL API from malicious queries with
                          automatic depth limiting at the edge.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{ flex: 2 }}
                    className='flex flex-col justify-between overflow-hidden bg-white rounded-lg shadow-lg md:flex-row lg:flex-col'
                  >
                    <div className='p-8 space-y-4'>
                      <Marketing.H3>Analytics</Marketing.H3>
                      <p className='text-base sm:text-lg text-gray opacity-80'>
                        Understand the traffic on your API. Quickly debug issues
                        with full insight into HTTP and GraphQL errors. Receive
                        alerts as soon as errors spike.
                      </p>
                    </div>
                    <img
                      src='/analytics-graph.svg'
                      className='max-w-sm lg:max-w-none'
                    />
                  </div>
                </div>
                <div className='sm:text-center'>
                  <Link href='/features'>
                    <a>
                      <Button className='sm:mx-auto'>Learn more</Button>
                    </a>
                  </Link>
                </div>
              </div>
            </div>

            <div className='flex justify-center px-6 my-24 space-x-16 sm:px-6 sm:my-32 lg:my-32 '>
              <Testimonial
                quote='We are seeing a major difference in server load and response times for our users. I would highly recommend GraphCDN to everyone with a GraphQL API!'
                name='Corjen Moll'
                title='Co-founder, Reversed Digital'
                twitter='corjenmoll'
              />
            </div>

            <div className='px-6 my-16 sm:px-6 sm:my-24 lg:my-32'>
              <div className='space-y-2 text-center'>
                <SubHeadline>Powered by Fastly's</SubHeadline>
                <Marketing.H2>
                  {networkLocations.length} worldwide edge locations
                </Marketing.H2>
              </div>
              <div className='mx-auto mt-24 max-w-7xl'>
                <Map />
              </div>
            </div>

            <div className='flex justify-center px-6 my-24 space-x-16 sm:px-6 sm:my-32 lg:my-32 '>
              <Testimonial
                quote='Caching responses has always been a pain for us but with GraphCDN we had it set up in minutes.'
                name='Eliezer Steinbock'
                title='Founder, Draft Fantasy'
                twitter='elie2222'
              />
            </div>

            <div className='relative px-6 pb-16 mt-16 sm:px-6 sm:mt-24 lg:mt-36 sm:pb-24 lg:pb-32'>
              <Letter>
                <p className='text-2xl font-medium leading-tight sm:text-4xl text-bg-blue'>
                  While building our last startups we struggled to scale,
                  inspect &amp; protect our GraphQL APIs.
                </p>
                <p>
                  As much as we love GraphQL for allowing us to ship good
                  products very quickly, no traditional CDN can cache GraphQL
                  APIs. We had to build custom caching solutions from scratch,
                  which distracted us for weeks and the caches never worked as
                  well as we needed them to.
                </p>
                <p>
                  <strong>That's why we built GraphCDN</strong> — so operators
                  like you get their well deserved peace of mind. Let us take
                  care of your scaling problems so you can rest knowing that
                  your GraphQL API is protected from random traffic spikes,
                  database troubles and downtime.
                </p>
              </Letter>
            </div>
          </main>
        </div>
      </div>

      <CTASection />
    </>
  )
}

function Testimonial({ twitter, name, title, quote }) {
  return (
    <div className='flex items-center max-w-3xl sm:space-x-8 gotham'>
      <div className='relative flex-shrink-0 hidden w-64 h-64 sm:block'>
        <Image
          className='flex-shrink-0'
          src={`https://res.cloudinary.com/graphcdn/image/twitter_name/${twitter}.jpg`}
          alt=''
          layout='fill'
          objectFit='cover'
        />
      </div>
      <div className='flex flex-col space-y-8 text-left'>
        <p className='text-xl font-medium leading-snug sm:text-2xl text-gray'>
          “{quote}”
        </p>
        <div className='space-y-1'>
          <p className='text-xs font-medium tracking-wider uppercase text-bg-blue'>
            {name}
          </p>
          <p className='text-xs font-medium tracking-wider uppercase opacity-50 text-gray'>
            {title}
          </p>
        </div>
      </div>
    </div>
  )
}

const Blue = ({ children }) => (
  <span style={{ color: '#79AAFF' }}>{children}</span>
)
const Green = ({ children }) => (
  <span style={{ color: '#15D1A8' }}>{children}</span>
)
const Gray = ({ children }) => (
  <span style={{ color: '#F8FAFC', opacity: '0.6' }}>{children}</span>
)

function RuleCode(props) {
  return (
    <pre className='font-mono'>
      <code>
        <div>
          <Blue>maxAge</Blue>
          <Gray>:</Gray> <Green>900</Green>
        </div>
        <div>
          <Blue>swr</Blue>
          <Gray>:</Gray> <Green>900</Green>
        </div>
        <div>
          <Blue>scope</Blue>
          <Gray>:</Gray> <Green>AUTHENTICATED</Green>
        </div>
        <div>
          <Blue>types</Blue>
          <Gray>:</Gray>
        </div>
        <div>
          {'  '}
          <Blue>Query</Blue>
          <Gray>:</Gray>
        </div>
        <div>
          {'    '}
          <Blue>getFeedback</Blue>
          <Gray>:</Gray> <Green>true</Green>
        </div>
        <div>
          {'  '}
          <Blue>Project</Blue>
          <Gray>:</Gray> <Green>true</Green>
        </div>
      </code>
    </pre>
  )
}

const Letter = ({ children }) => (
  <div className='relative max-w-3xl mx-auto'>
    <div className='space-y-8 text-lg text-gray opacity-80 gotham'>
      {children}
    </div>

    <div className='flex flex-col mt-8 space-y-8 sm:flex-row sm:space-y-0 sm:items-end sm:space-x-8'>
      <div className='flex items-center flex-1 mt-8'>
        <Image
          src='/tim.jpg'
          alt=''
          width={80}
          height={80}
          className='rounded-full'
        />
        <div className='flex flex-col items-start justify-start ml-4'>
          <TimSignature
            className='h-16 max-w-full fill-current text-gray'
            style={{ zIndex: 1 }}
          />
          <div className='mt-1 text-xs font-medium tracking-wider uppercase gotham text-gray'>
            Tim Suchanek <span className='ml-1 opacity-50'>Co-founder</span>
          </div>
        </div>
      </div>
      <div className='flex items-center flex-1 mt-8'>
        <Image
          src='/mxstbr.jpg'
          alt=''
          width={80}
          height={80}
          className='rounded-full'
        />
        <div className='flex flex-col items-start justify-start ml-4'>
          <MaxSignature
            className='h-24 max-w-full fill-current text-gray'
            style={{ marginBottom: `-2.5em`, zIndex: 1 }}
          />
          <div className='mt-1 text-xs font-medium tracking-wider uppercase gotham text-gray'>
            {' '}
            Max Stoiber <span className='ml-1 opacity-50'>Co-founder</span>
          </div>
        </div>
      </div>
    </div>
  </div>
)
